import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { extractArray } from "../utils.js/utils";

/**
 * Shared searchable dropdown — safe against non-array `items` payloads from API.
 */
const DropdownWithSearch = ({
  isOpen,
  onToggle,
  searchTerm = "",
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  currentPage = 1,
  onPageChange,
  onSelect,
  label,
  renderItem,
  isRTL,
  t: tProp,
  emptyMessage,
  menuStyle,
  inputStyle,
  itemStyle,
}) => {
  const { t: tHook, i18n } = useTranslation("global");
  const t = tProp || tHook;
  const rtl = isRTL ?? i18n.language === "ar";
  const safeItems = extractArray(items);

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, searchInputRef]);

  if (!isOpen) return null;

  const renderDropdownItemDefault = item => {
    const code = item.code || "";
    const name = rtl
      ? item.name_ar || item.name_en || item.name || ""
      : item.name_en || item.name_ar || item.name || "";
    return `${code ? `${code} - ` : ""}${name}`;
  };

  const defaultMenuStyle = {
    position: "absolute",
    zIndex: 10,
    top: "100%",
    left: 0,
    right: 0,
    background: "#1a1f2e",
    border: "1px solid #333",
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 400,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...menuStyle,
  };

  const defaultInputStyle = {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #333",
    backgroundColor: "#14263b",
    color: "white",
    fontSize: 14,
    direction: rtl ? "rtl" : "ltr",
    textAlign: rtl ? "right" : "left",
    ...inputStyle,
  };

  const defaultItemStyle = {
    padding: "10px 12px",
    cursor: "pointer",
    borderTop: "1px solid #333",
    color: "white",
    direction: rtl ? "rtl" : "ltr",
    textAlign: rtl ? "right" : "left",
    ...itemStyle,
  };

  const lastPage = pagination?.last_page || 1;
  const curPage = currentPage || 1;

  return (
    <div style={defaultMenuStyle}>
      <div style={{ padding: 8, flexShrink: 0 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t("common.search", { defaultValue: rtl ? "ابحث عن" : "Search" })} ${label || ""}`}
          value={searchTerm}
          onChange={e => onSearchChange?.(e.target.value)}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={defaultInputStyle}
        />
      </div>

      {pagination && lastPage > 1 && (
        <div
          style={{
            padding: "8px",
            borderTop: "1px solid #333",
            background: "#14263b",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (curPage > 1) onPageChange?.(1);
              }}
              disabled={curPage === 1}
              style={{
                padding: "4px 8px",
                backgroundColor: curPage === 1 ? "#2a2a2a" : "#1a1f2e",
                color: curPage === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: 4,
                cursor: curPage === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (curPage > 1) onPageChange?.(curPage - 1);
              }}
              disabled={curPage === 1}
              style={{
                padding: "4px 8px",
                backgroundColor: curPage === 1 ? "#2a2a2a" : "#1a1f2e",
                color: curPage === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: 4,
                cursor: curPage === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (curPage < lastPage) onPageChange?.(curPage + 1);
              }}
              disabled={curPage >= lastPage}
              style={{
                padding: "4px 8px",
                backgroundColor: curPage >= lastPage ? "#2a2a2a" : "#1a1f2e",
                color: curPage >= lastPage ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: 4,
                cursor: curPage >= lastPage ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (curPage < lastPage) onPageChange?.(lastPage);
              }}
              disabled={curPage >= lastPage}
              style={{
                padding: "4px 8px",
                backgroundColor: curPage >= lastPage ? "#2a2a2a" : "#1a1f2e",
                color: curPage >= lastPage ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: 4,
                cursor: curPage >= lastPage ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
            >
              »»
            </button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {safeItems.length === 0 ? (
          <div style={{ padding: 12, textAlign: "center", color: "#bbb" }}>
            {emptyMessage ||
              t("common.noResults", {
                defaultValue: rtl ? "لا توجد نتائج" : "No results",
              })}
          </div>
        ) : (
          safeItems.map(item => (
            <div
              key={item.id ?? `${item.code}-${item.name}`}
              onClick={e => {
                e.stopPropagation();
                onSelect?.(item.id ?? item);
                onToggle?.(false);
              }}
              style={defaultItemStyle}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#2a2a2a";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {renderItem ? renderItem(item) : renderDropdownItemDefault(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DropdownWithSearch;
